import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
    ChevronRight, ArrowRight, Clock, Search, Mail, TrendingUp, Play, Bike, Car, Star, Target, Folder, Ticket,
    BookOpen, MapPin, UtensilsCrossed, Lightbulb, Plane, Newspaper, Map, Camera, Coffee, Heart, Globe
} from 'lucide-react'

// Helper to render category icon from icon name
const iconMap: Record<string, any> = {
    'bookopen': BookOpen, 'book-open': BookOpen,
    'mappin': MapPin, 'map-pin': MapPin,
    'utensilscrossed': UtensilsCrossed, 'utensils-crossed': UtensilsCrossed,
    'lightbulb': Lightbulb, 'plane': Plane, 'newspaper': Newspaper,
    'folder': Folder, 'map': Map, 'camera': Camera, 'coffee': Coffee,
    'star': Star, 'heart': Heart, 'globe': Globe, 'target': Target,
}
const renderCatIcon = (iconName: string | undefined, className = 'w-4 h-4') => {
    if (!iconName) return <Folder className={className} />
    const IconComponent = iconMap[iconName.toLowerCase().trim()] || Folder
    return <IconComponent className={className} />
}
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { newsApi, vehiclesApi, toursApi } from '../../lib/api'
import { formatPrice } from '../../lib/utils'
import type { SectionConfig, PageLayout } from '../../lib/sections'
import {
    HeroSection,
    CategorySection,
    ToursSection,
    VehiclesSection,
    VideosSection,
    VideoSection,
    SearchSection,
    CategoriesSection,
    PopularSection,
    CTASection,
    NewsletterSection,
    AdsSection
} from '../../components/sections/SectionRenderers'

interface Article {
    id: number
    slug: string
    title_en: string
    title_vi: string
    excerpt_en: string
    excerpt_vi: string
    category: string
    featured_image: string
    created_at: string
}

interface Vehicle {
    id: number
    brand: string
    model: string
    type: string
    dailyRate: number
    images: string[]
    slug: string
}

interface Tour {
    id: number
    title_en: string
    title_vi: string
    price: number
    duration: string
    featuredImage: string
}

interface Category {
    id: number
    slug: string
    name_en: string
    name_vi: string
    icon: string
}

export default function BlogHomePage() {
    const { language } = useLanguageStore()
    const { currency, exchangeRate } = useCurrencyStore()

    const [layout, setLayout] = useState<PageLayout | null>(null)
    const [articles, setArticles] = useState<Article[]>([])
    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [tours, setTours] = useState<Tour[]>([])
    const [categories, setCategories] = useState<Category[]>([])
    const [loading, setLoading] = useState(true)

    // Fetch layout config and data
    useEffect(() => {
        Promise.all([
            fetch('/api/page-layouts/home').then(r => r.json()).catch(() => null),
            newsApi.list({ limit: 50 }),
            vehiclesApi.list({ limit: 10 }),
            toursApi.list({ limit: 8 }),
            fetch('/api/categories').then(r => r.json())
        ]).then(([layoutRes, newsRes, vehiclesRes, toursRes, catRes]) => {
            if (layoutRes?.success) {
                setLayout(layoutRes.layout)
            }
            setArticles(newsRes.data?.data || [])
            setVehicles(vehiclesRes.data?.vehicles || [])
            setTours(toursRes.data?.tours || [])
            setCategories(catRes.categories || [])
        }).finally(() => setLoading(false))
    }, [])

    // Group articles by category
    const articlesByCategory = useMemo(() => {
        const grouped: Record<string, Article[]> = {}
        categories.forEach(cat => {
            grouped[cat.slug] = articles.filter(a => a.category === cat.slug)
        })
        return grouped
    }, [articles, categories])

    // Get section data helper
    const getSectionData = (section: SectionConfig) => {
        switch (section.type) {
            case 'hero':
                return { articles: articles.slice(0, section.config?.count || 5) }
            case 'category':
                const cat = categories.find(c => c.slug === section.config?.categorySlug)
                return {
                    category: cat,
                    articles: articlesByCategory[section.config?.categorySlug]?.slice(0, section.config?.count || 3) || []
                }
            case 'tours':
                return { tours: tours.slice(0, section.config?.count || 4) }
            case 'vehicles':
                return { vehicles: vehicles.slice(0, section.config?.count || 6) }
            default:
                return {}
        }
    }

    // Render main content section
    const renderSection = (section: SectionConfig) => {
        const data = getSectionData(section)
        const props = { config: section, data, language, currency, exchangeRate }

        switch (section.type) {
            case 'hero':
                return <HeroSection key={section.id} {...props} />
            case 'category':
                return <CategorySection key={section.id} {...props} />
            case 'tours':
                return <ToursSection key={section.id} {...props} />
            case 'vehicles':
                return <VehiclesSection key={section.id} {...props} />
            case 'videos':
                return <VideosSection key={section.id} {...props} />
            default:
                return null
        }
    }

    // Render sidebar section
    const renderSidebarSection = (section: SectionConfig) => {
        const data = { categories, articles, articlesByCategory }

        switch (section.type) {
            case 'search':
                return <SearchSection key={section.id} language={language} />
            case 'categories':
                return <CategoriesSection key={section.id} data={data} language={language} config={section.config} />
            case 'popular':
                return <PopularSection key={section.id} data={data} language={language} />
            case 'cta':
                return <CTASection key={section.id} config={section.config} language={language} />
            case 'newsletter':
                return <NewsletterSection key={section.id} language={language} sticky={section.sticky} />
            case 'ads':
                return <AdsSection key={section.id} config={section.config} />
            case 'videos':
                return <VideoSection key={section.id} language={language} config={section.config} />
            default:
                return null
        }
    }

    // Default layout sections if no config
    const mainSections = layout?.layout_config?.sections || []
    const sidebarSections = layout?.sidebar_config?.sections || []

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
            </div>
        )
    }

    // Fallback to static layout if no dynamic config
    if (mainSections.length === 0) {
        return <StaticHomePage
            articles={articles}
            vehicles={vehicles}
            tours={tours}
            categories={categories}
            articlesByCategory={articlesByCategory}
            language={language}
            currency={currency}
            exchangeRate={exchangeRate}
        />
    }

    return (
        <>
            <Helmet>
                <title>{language === 'en' ? 'Phú Quốc Travel Blog | Tours & Vehicle Rental' : 'Blog Du Lịch Phú Quốc | Tour & Thuê Xe'}</title>
                <meta name="description" content={language === 'en'
                    ? 'Discover Phú Quốc with travel guides, local tips, tours, and vehicle rental.'
                    : 'Khám phá Phú Quốc với cẩm nang du lịch, mẹo hay, tour và thuê xe.'
                } />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Hero Section - full width, outside grid */}
                {mainSections.filter(s => s.type === 'hero').map(section => renderSection(section))}

                {/* Main Content with Sidebar */}
                <div className="container mx-auto px-4 py-6 md:py-8">
                    <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
                        {/* Main Content - 2/3 */}
                        <div className="lg:col-span-2 space-y-6">
                            {mainSections.filter(s => s.type !== 'hero').map(section => renderSection(section))}
                        </div>

                        {/* Sidebar - Hidden on mobile, visible on lg */}
                        <aside className="hidden lg:block space-y-4 lg:sticky lg:top-20 lg:self-start">
                            {sidebarSections.map(section => renderSidebarSection(section))}
                        </aside>
                    </div>
                </div>
            </div>
        </>
    )
}

// Static fallback homepage - Mobile-first design
function StaticHomePage({ articles, vehicles, tours, categories, articlesByCategory, language, currency, exchangeRate }: any) {
    const featured = articles[0]
    const getTitle = (a: Article) => language === 'en' ? a.title_en : (a.title_vi || a.title_en)
    const getCatName = (c: Category) => language === 'en' ? c.name_en : c.name_vi

    return (
        <>
            <Helmet>
                <title>{language === 'en' ? 'Phú Quốc Travel Blog' : 'Blog Du Lịch Phú Quốc'}</title>
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Hero - Simple mobile-first */}
                {featured && (
                    <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
                        <div className="container mx-auto px-4 py-6 md:py-10">
                            <div className="flex flex-col md:flex-row gap-6 items-center">
                                <div className="flex-1 text-center md:text-left">
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-500 rounded-full text-xs font-medium mb-3">
                                        <Star className="w-3 h-3" /> {language === 'en' ? 'Featured' : 'Nổi bật'}
                                    </span>
                                    <h1 className="text-xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight line-clamp-3">
                                        {getTitle(featured)}
                                    </h1>
                                    <Link
                                        to={`/blog/${featured.category}/${featured.slug}`}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary-700 font-semibold rounded-lg hover:bg-orange-500 hover:text-white transition text-sm"
                                    >
                                        {language === 'en' ? 'Read More' : 'Đọc thêm'} <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                                <div className="w-full md:w-1/2 lg:w-2/5">
                                    <img
                                        src={featured.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=800'}
                                        alt={getTitle(featured)}
                                        className="w-full h-[180px] md:h-[250px] object-cover rounded-xl shadow-lg"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Main Content */}
                <div className="container mx-auto px-4 py-6">
                    <div className="flex flex-col lg:flex-row gap-6">

                        {/* Main Column */}
                        <div className="flex-1 min-w-0 space-y-6">

                            {/* Category Sections - Simple list on mobile */}
                            {categories.slice(0, 3).map((cat: Category) => {
                                const catArticles = articlesByCategory[cat.slug]?.slice(0, 4) || []
                                if (catArticles.length === 0) return null

                                return (
                                    <section key={cat.id} className="bg-white rounded-xl p-4 border shadow-sm">
                                        {/* Header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <h2 className="text-base md:text-lg font-bold flex items-center gap-2">
                                                {renderCatIcon(cat.icon, 'w-5 h-5')}
                                                <span className="truncate">{getCatName(cat)}</span>
                                            </h2>
                                            <Link to={`/blog/${cat.slug}`} className="text-primary-600 text-xs font-medium flex items-center gap-1 whitespace-nowrap">
                                                {language === 'en' ? 'View all' : 'Xem tất cả'} <ChevronRight className="w-3 h-3" />
                                            </Link>
                                        </div>

                                        {/* Articles - Simple stacked list */}
                                        <div className="space-y-3">
                                            {catArticles.map((article: Article, idx: number) => (
                                                <Link
                                                    key={article.id}
                                                    to={`/blog/${article.category}/${article.slug}`}
                                                    className="flex gap-3 group"
                                                >
                                                    <img
                                                        src={article.featured_image || 'https://images.unsplash.com/photo-1559628233-100c798642d4?w=300'}
                                                        alt={getTitle(article)}
                                                        className={`object-cover rounded-lg flex-shrink-0 ${idx === 0 ? 'w-24 h-20 md:w-32 md:h-24' : 'w-20 h-16 md:w-24 md:h-20'}`}
                                                    />
                                                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                        <h4 className={`font-semibold line-clamp-2 group-hover:text-primary-600 transition ${idx === 0 ? 'text-sm md:text-base' : 'text-xs md:text-sm'}`}>
                                                            {getTitle(article)}
                                                        </h4>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </section>
                                )
                            })}

                            {/* Tours - Horizontal scroll */}
                            {tours.length > 0 && (
                                <section className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 text-white">
                                    <div className="flex items-center justify-between mb-3">
                                        <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Ticket className="w-5 h-5" /> {language === 'en' ? 'Popular Tours' : 'Tour Nổi Bật'}</h2>
                                        <Link to="/tours" className="text-white/80 hover:text-white text-xs font-medium flex items-center gap-1">
                                            {language === 'en' ? 'All' : 'Tất cả'} <ChevronRight className="w-3 h-3" />
                                        </Link>
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
                                        {tours.slice(0, 4).map((tour: Tour) => (
                                            <Link
                                                key={tour.id}
                                                to={`/tours/${tour.id}`}
                                                className="flex-shrink-0 w-36 bg-white/10 backdrop-blur rounded-lg overflow-hidden hover:bg-white/20 transition"
                                            >
                                                <div className="h-20 overflow-hidden">
                                                    <img src={tour.featuredImage || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400'} alt="" className="w-full h-full object-cover" />
                                                </div>
                                                <div className="p-2">
                                                    <h3 className="font-medium text-xs line-clamp-2 mb-1">{language === 'en' ? tour.title_en : (tour.title_vi || tour.title_en)}</h3>
                                                    <div className="flex items-center justify-between text-[10px]">
                                                        <span className="opacity-80 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{tour.duration}</span>
                                                        <span className="font-bold">{formatPrice(tour.price, currency, exchangeRate)}</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Vehicles - Horizontal scroll */}
                            {vehicles.length > 0 && (
                                <section className="bg-white rounded-xl p-4 border shadow-sm">
                                    <div className="flex items-center justify-between mb-3">
                                        <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Bike className="w-5 h-5" /> {language === 'en' ? 'Rent a Vehicle' : 'Thuê Xe'}</h2>
                                        <Link to="/vehicles" className="text-primary-600 text-xs font-medium flex items-center gap-1">
                                            {language === 'en' ? 'All' : 'Tất cả'} <ChevronRight className="w-3 h-3" />
                                        </Link>
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
                                        {vehicles.slice(0, 6).map((v: Vehicle) => (
                                            <Link
                                                key={v.id}
                                                to={`/vehicles/${v.slug || v.id}`}
                                                className="flex-shrink-0 w-32 border rounded-lg overflow-hidden hover:shadow-md transition bg-white"
                                            >
                                                <div className="h-20 bg-gray-100 flex items-center justify-center overflow-hidden">
                                                    {v.images?.[0] ? (
                                                        <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        v.type === 'motorcycle' ? <Bike className="w-6 h-6 text-gray-300" /> : <Car className="w-6 h-6 text-gray-300" />
                                                    )}
                                                </div>
                                                <div className="p-2">
                                                    <h4 className="font-bold text-xs line-clamp-1 mb-0.5">{v.brand} {v.model}</h4>
                                                    <span className="text-primary-600 font-bold text-xs">
                                                        {formatPrice(v.dailyRate, currency, exchangeRate)}
                                                        <span className="text-gray-400 font-normal text-[10px]">/ngày</span>
                                                    </span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Videos - Horizontal scroll */}
                            <section className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl p-4 text-white">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-base md:text-lg font-bold flex items-center gap-2">
                                        <Play className="w-4 h-4" /> {language === 'en' ? 'Short Videos' : 'Video Ngắn'}
                                    </h2>
                                    <Link to="/shorts" className="text-white/80 hover:text-white text-xs font-medium flex items-center gap-1">
                                        {language === 'en' ? 'Watch all' : 'Xem tất cả'} <ChevronRight className="w-3 h-3" />
                                    </Link>
                                </div>
                                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <Link
                                            key={i}
                                            to="/shorts"
                                            className="flex-shrink-0 w-24 h-36 bg-white/10 backdrop-blur rounded-lg flex items-center justify-center hover:bg-white/20 transition"
                                        >
                                            <Play className="w-6 h-6" />
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        </div>

                        {/* Sidebar - Hidden on mobile, visible on lg */}
                        <aside className="hidden lg:block w-72 space-y-4 flex-shrink-0">
                            {/* Search */}
                            <div className="bg-white rounded-xl p-4 border shadow-sm">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input type="text" placeholder={language === 'en' ? 'Search...' : 'Tìm kiếm...'} className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm" />
                                </div>
                            </div>

                            {/* Categories */}
                            <div className="bg-white rounded-xl p-4 border shadow-sm">
                                <h3 className="font-bold mb-3 text-sm flex items-center gap-2"><Folder className="w-4 h-4" /> {language === 'en' ? 'Categories' : 'Danh mục'}</h3>
                                <div className="space-y-1">
                                    {categories.map((cat: Category) => (
                                        <Link key={cat.id} to={`/blog/${cat.slug}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition text-sm">
                                            <span className="flex items-center gap-2">
                                                {renderCatIcon(cat.icon)}
                                                <span>{getCatName(cat)}</span>
                                            </span>
                                            <span className="text-xs text-gray-400">{articlesByCategory[cat.slug]?.length || 0}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Popular */}
                            <div className="bg-white rounded-xl p-4 border shadow-sm">
                                <h3 className="font-bold mb-3 flex items-center gap-2 text-sm">
                                    <TrendingUp className="w-4 h-4 text-primary-600" />{language === 'en' ? 'Popular' : 'Phổ biến'}
                                </h3>
                                <div className="space-y-2">
                                    {articles.slice(0, 5).map((a: Article, idx: number) => (
                                        <Link key={a.id} to={`/blog/${a.category}/${a.slug}`} className="flex gap-2 group">
                                            <span className="w-5 h-5 flex items-center justify-center bg-primary-100 text-primary-600 font-bold rounded text-xs flex-shrink-0">{idx + 1}</span>
                                            <h4 className="text-xs font-medium line-clamp-2 group-hover:text-primary-600 transition">{getTitle(a)}</h4>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
                                <h3 className="font-bold text-sm mb-2 flex items-center gap-1"><Target className="w-4 h-4" /> {language === 'en' ? 'Book a Tour!' : 'Đặt Tour Ngay!'}</h3>
                                <p className="text-xs opacity-90 mb-3">{language === 'en' ? 'Explore Phú Quốc with guided tours.' : 'Khám phá Phú Quốc với tour.'}</p>
                                <Link to="/tours" className="block w-full py-2 bg-white text-orange-600 font-semibold rounded-lg text-center hover:bg-gray-100 transition text-xs">
                                    {language === 'en' ? 'Explore Tours' : 'Xem Tour'}
                                </Link>
                            </div>

                            {/* Newsletter */}
                            <div className="bg-white rounded-xl p-4 border shadow-sm sticky top-4">
                                <h3 className="font-bold mb-2 flex items-center gap-2 text-sm">
                                    <Mail className="w-4 h-4 text-primary-600" />{language === 'en' ? 'Newsletter' : 'Đăng ký'}
                                </h3>
                                <p className="text-xs text-gray-600 mb-3">{language === 'en' ? 'Get travel tips & deals' : 'Nhận mẹo du lịch & ưu đãi'}</p>
                                <form className="space-y-2">
                                    <input type="email" placeholder="Email" className="w-full px-3 py-2 border rounded-lg text-xs" />
                                    <button className="w-full py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition text-xs">
                                        {language === 'en' ? 'Subscribe' : 'Đăng ký'}
                                    </button>
                                </form>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </>
    )
}
